import { Suspense, lazy, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { GuestRoute } from "@/components/auth/GuestRoute";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { WeddingWorkspace, WeddingModuleRedirect } from "@/components/layout/WeddingWorkspace";
import { AppShell } from "@/components/layout/AppShell";
import { LoadingScreen } from "@/components/common/LoadingScreen";
import { EventDetailRedirect } from "@/components/common/EventDetailRedirect";

import { LoginPage } from "@/features/auth/LoginPage";
import { SignupPage } from "@/features/auth/SignupPage";
import { ConfirmEmailPage } from "@/features/auth/ConfirmEmailPage";
import { ForgotPasswordPage } from "@/features/auth/ForgotPasswordPage";
import { LandingPage } from "@/features/landing/LandingPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { SettingsPage } from "@/features/settings/SettingsPage";

import { NotFoundPage } from "@/pages/NotFoundPage";
import { UnauthorizedPage } from "@/pages/UnauthorizedPage";

/**
 * Every future module is lazy-loaded: each is its own chunk, fetched only
 * when a person actually navigates there. With 18+ modules on the roadmap,
 * this keeps the initial bundle to auth + dashboard + shell, and scales to
 * many more modules without the entry bundle growing in step.
 */
const OrganizationsPage = lazy(() =>
  import("@/features/organizations/OrganizationsPage").then((m) => ({ default: m.OrganizationsPage }))
);
const WeddingsPage = lazy(() =>
  import("@/features/weddings/WeddingsPage").then((m) => ({ default: m.WeddingsPage }))
);
const WeddingDetailPage = lazy(() =>
  import("@/features/weddings/WeddingDetailPage").then((m) => ({ default: m.WeddingDetailPage }))
);
const GuestsPage = lazy(() =>
  import("@/features/guests/GuestsPage").then((m) => ({ default: m.GuestsPage }))
);
const BudgetPage = lazy(() =>
  import("@/features/budget/BudgetPage").then((m) => ({ default: m.BudgetPage }))
);
const ChecklistPage = lazy(() =>
  import("@/features/checklist/ChecklistPage").then((m) => ({ default: m.ChecklistPage }))
);
const RunOfShowPage = lazy(() =>
  import("@/features/run-of-show/RunOfShowPage").then((m) => ({ default: m.RunOfShowPage }))
);
const EventLayoutPage = lazy(() =>
  import("@/features/layout/EventLayoutPage").then((m) => ({ default: m.EventLayoutPage }))
);
const VendorsPage = lazy(() =>
  import("@/features/vendors/VendorsPage").then((m) => ({ default: m.VendorsPage }))
);
const VendorDirectoryPage = lazy(() =>
  import("@/features/vendors/VendorDirectoryPage").then((m) => ({ default: m.VendorDirectoryPage }))
);
const DocumentsPage = lazy(() =>
  import("@/features/documents/DocumentsPage").then((m) => ({ default: m.DocumentsPage }))
);
const MoodboardPage = lazy(() =>
  import("@/features/moodboard/MoodboardPage").then((m) => ({ default: m.MoodboardPage }))
);
const NotificationsPage = lazy(() =>
  import("@/features/notifications/NotificationsPage").then((m) => ({ default: m.NotificationsPage }))
);
const AiAssistantPage = lazy(() =>
  import("@/features/ai-assistant/AiAssistantPage").then((m) => ({ default: m.AiAssistantPage }))
);
const BillingPage = lazy(() =>
  import("@/features/billing/BillingPage").then((m) => ({ default: m.BillingPage }))
);

function LazyPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LoadingScreen label="Loading module…" />}>{children}</Suspense>;
}

/**
 * Route tree. Every future module already has a real route pointed at its
 * ComingSoon placeholder — building the feature later means swapping the
 * page component import, not touching this file's structure.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />

      <Route element={<GuestRoute />}>
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/settings" element={<SettingsPage />} />

          <Route path="/organizations" element={<LazyPage><OrganizationsPage /></LazyPage>} />

          {/* Generic event routes — redirect into the wedding vertical until a second vertical exists. */}
          <Route path="/events" element={<Navigate to="/weddings" replace />} />
          <Route path="/events/:eventId" element={<EventDetailRedirect />} />

          <Route path="/weddings" element={<LazyPage><WeddingsPage /></LazyPage>} />
          <Route path="/vendors" element={<LazyPage><VendorDirectoryPage /></LazyPage>} />
          <Route path="/weddings/:weddingId" element={<WeddingWorkspace />}>
            <Route index element={<LazyPage><WeddingDetailPage /></LazyPage>} />
            <Route path="guests" element={<LazyPage><GuestsPage /></LazyPage>} />
            <Route path="rsvp" element={<WeddingModuleRedirect modulePath="/guests?view=confirmations" />} />
            <Route path="budget" element={<LazyPage><BudgetPage /></LazyPage>} />
            <Route path="planning" element={<LazyPage><ChecklistPage /></LazyPage>} />
            <Route path="timeline" element={<WeddingModuleRedirect modulePath="/planning" />} />
            <Route path="checklist" element={<WeddingModuleRedirect modulePath="/planning" />} />
            <Route path="run-of-show" element={<LazyPage><RunOfShowPage /></LazyPage>} />
            <Route path="layout" element={<LazyPage><EventLayoutPage /></LazyPage>} />
            <Route path="vendors" element={<LazyPage><VendorsPage /></LazyPage>} />
            <Route path="contracts" element={<WeddingModuleRedirect modulePath="/documents?view=contracts" />} />
            <Route path="payments" element={<WeddingModuleRedirect modulePath="/documents?view=payments" />} />
            <Route path="documents" element={<LazyPage><DocumentsPage /></LazyPage>} />
            <Route path="moodboard" element={<LazyPage><MoodboardPage /></LazyPage>} />
          </Route>
          <Route path="/guests" element={<WeddingModuleRedirect modulePath="/guests" />} />
          <Route path="/rsvp" element={<WeddingModuleRedirect modulePath="/guests?view=confirmations" />} />
          <Route path="/budget" element={<WeddingModuleRedirect modulePath="/budget" />} />
          <Route path="/planning" element={<WeddingModuleRedirect modulePath="/planning" />} />
          <Route path="/timeline" element={<WeddingModuleRedirect modulePath="/planning" />} />
          <Route path="/checklist" element={<WeddingModuleRedirect modulePath="/planning" />} />
          <Route path="/run-of-show" element={<WeddingModuleRedirect modulePath="/run-of-show" />} />
          <Route path="/layout" element={<WeddingModuleRedirect modulePath="/layout" />} />
          <Route path="/contracts" element={<WeddingModuleRedirect modulePath="/documents?view=contracts" />} />
          <Route path="/payments" element={<WeddingModuleRedirect modulePath="/documents?view=payments" />} />
          <Route path="/documents" element={<WeddingModuleRedirect modulePath="/documents" />} />
          <Route path="/moodboard" element={<WeddingModuleRedirect modulePath="/moodboard" />} />
          <Route path="/notifications" element={<LazyPage><NotificationsPage /></LazyPage>} />
          <Route path="/ai-assistant" element={<LazyPage><AiAssistantPage /></LazyPage>} />
          <Route path="/billing" element={<LazyPage><BillingPage /></LazyPage>} />
        </Route>
      </Route>

      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
